import React from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { SalaryDeviationConstants } from "utils/constants";
import { User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { CommissionSalaryListViewProps } from "models/propTypes/salaryDeviationPropTypes";
import { CurrencyEnums } from "models/enums";
//#endregion
//#region components
import { TableAsync } from "components/common";
import { useCreateAllowanceDialog } from "components/salaryDeviation";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
import { useCommissionSalaryService } from "serviceHooks";
import type { CommissionSalaryRow } from "serviceHooks/useCommissionSalaryService";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const CommissionSalaryListView: React.FC<CommissionSalaryListViewProps> = (props) => {
  const commissionSalaryService = useCommissionSalaryService();
  const { isWintSalaryCommissionAvailable } = useSalaryPermissions();
  const { filter, reloadList, reloadKey } = props;

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const filterFunc = React.useCallback(
    (baseFilter: WintUtilsWebApi.Filter) =>
      commissionSalaryService.reportFilter(baseFilter, {
        ...(filter ?? {}),
      }),
    // isWintSalaryCommissionAvailable selects which endpoint reportFilter reads from, so it must
    // invalidate this memo; otherwise a company switch / permission change keeps the stale closure
    // pointing at the previous endpoint.
    [filter, isWintSalaryCommissionAvailable]
  );

  const openAllowanceDialog = useCreateAllowanceDialog(reloadList);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars --- implemented for later use
  const onRowClicked = (row: WintSuperkollAPIModel.SalaryDeviationDescriptor, rowIndex: number) => {
    dispatch(
      displayDialog(
        openAllowanceDialog({
          salaryDeviationId: row.id,
          deviationTypes: SalaryDeviationConstants.CommissionTypes,
          isAdminOnlyType: row.isAdminOnlyType,
        })
      )
    );
  };

  return (
    <TableAsync<CommissionSalaryRow>
      properties={{
        personName: {
          description: TranslationKeys.Common.Employee,
          formatter: (transaction: CommissionSalaryRow) => new User(transaction.personId, transaction.personName),
        },
        comment: {
          description: TranslationKeys.Common.Description,
          // Wint.Salary can't sort by the comment field server-side, so hide the otherwise-inert
          // sort affordance for migrated companies; the legacy engine sorts it fine.
          disableSort: isWintSalaryCommissionAvailable,
        },
        date: {
          description: TranslationKeys.Common.Date,
          formatter: (transaction: CommissionSalaryRow) => transaction.date && new Date(transaction.date),
          noWrap: true,
        },
        amount: {
          description: TranslationKeys.Common.Amount,
          formatter: (transaction: CommissionSalaryRow) =>
            transaction.amount == null
              ? "-"
              : LocalizationHelperInstance.getFormattedCurrency(transaction.amount, CurrencyEnums.CurrencyCode.SEK),
          alignment: "right",
        },
        commission: {
          description: TranslationKeys.CommissionSalary.Commission,
          formatter: (transaction: CommissionSalaryRow) =>
            LocalizationHelperInstance.getFormattedCurrency(transaction.commission, CurrencyEnums.CurrencyCode.SEK),
          alignment: "right",
          className: cssExports.textBlack,
          classification: "amount",
        },
      }}
      filter={filterFunc}
      maxRowCount={10}
      pagination="advanced"
      reloadKey={reloadKey}
    />
  );
};
export default CommissionSalaryListView;
